import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { AuthContext } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  ExternalLinkIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  Share2Icon,
  ChevronDownIcon,
} from 'lucide-react';
import CommentFeed from '../components/campaign/CommentFeed';
import ContributionSidebar from '../components/campaign/ContributionSidebar';
import SignInPromptModal from '../components/campaign/modals/SignInPromptModal';
import OrgConfirmModal from '../components/campaign/modals/OrgConfirmModal';
import OrgSelectModal from '../components/campaign/modals/OrgSelectModal';
import Button from '../components/common/Button';
import CampaignFallbackImage from '../components/campaign/CampaignFallbackImage';
import CampaignShareMenu from '../components/campaign/CampaignShareMenu';
import { useUserProfile } from '../hooks/useUserProfile';
import { useCampaignComments } from '../hooks/useCampaignComments';
import { trackEvent, ANALYTICS_EVENTS, trackPageView } from '../utils/analytics';
import { getSourceFallback, GENERIC_FALLBACK_IMAGE } from '../utils/sourcecFallbacks';
import { shouldShowVoteCounts } from '../utils/voteDisplay';

const SITE_URL = 'https://ballotr.com';
const DEFAULT_SOCIAL_IMAGE = `${SITE_URL}/logo192.png`;
const MOBILE_BREAKPOINT = 768;

const toAbsoluteUrl = (url?: string | null) => {
  if (!url) return DEFAULT_SOCIAL_IMAGE;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `${SITE_URL}${url}`;
  return `${SITE_URL}/${url}`;
};

const truncateDescription = (value?: string | null, maxLength = 160) => {
  if (!value) return '';
  const normalized = value.replace(/\s+/g, ' ').trim();
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 1).trim()}…`;
};

const CampaignDetail: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  const { profile } = useUserProfile();

  const [campaign, setCampaign] = useState<any>(null);
  const [highlights, setHighlights] = useState<any>({});
  const [showPrompt, setShowPrompt] = useState(false);
  const [showOrgConfirm, setShowOrgConfirm] = useState(false);
  const [showOrgSelect, setShowOrgSelect] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingAction, setPendingAction] = useState<'support' | 'oppose' | null>(null);

  const [userVote, setUserVote] = useState<'upvote' | 'downvote' | null>(null);
  const [upvoteCount, setUpvoteCount] = useState(0);
  const [downvoteCount, setDownvoteCount] = useState(0);
  const [imgFailed, setImgFailed] = useState(false);

  const [showDesktopShareMenu, setShowDesktopShareMenu] = useState(false);
  const [showMobileShareSheet, setShowMobileShareSheet] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < MOBILE_BREAKPOINT;
  });

  const [newComment, setNewComment] = useState('');
  const commentTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  const {
    comments,
    loadingComments,
    submittingComment,
    canComment,
    addComment,
  } = useCampaignComments({
    campaignId: id,
    userId: user?.id,
  });

  const normalizedImage = useMemo(
    () => (campaign?.image || '').trim().toLowerCase(),
    [campaign?.image]
  );

  const normalizedGenericFallback = useMemo(
    () => GENERIC_FALLBACK_IMAGE.trim().toLowerCase(),
    []
  );

  const normalizedSourceFallback = useMemo(() => {
    const fallback = getSourceFallback(campaign?.source);
    return fallback ? fallback.trim().toLowerCase() : null;
  }, [campaign?.source]);

  const isRemotePlaceholderFallback = useMemo(() => {
    return normalizedImage.includes('placehold.co/') && normalizedImage.includes('text=ballotr');
  }, [normalizedImage]);

  const isFallbackAsset = useMemo(() => {
    if (!normalizedImage) return false;

    return (
      normalizedImage === normalizedGenericFallback ||
      (!!normalizedSourceFallback && normalizedImage === normalizedSourceFallback) ||
      isRemotePlaceholderFallback
    );
  }, [
    normalizedImage,
    normalizedGenericFallback,
    normalizedSourceFallback,
    isRemotePlaceholderFallback,
  ]);

  const hasImage = useMemo(
    () => !!campaign?.image && campaign.image.trim().length > 5,
    [campaign?.image]
  );

  const showFallback = !hasImage || imgFailed || isFallbackAsset;
  const showVoteCounts = shouldShowVoteCounts({
    userVote,
    upvotes: upvoteCount,
    downvotes: downvoteCount,
  });

  const canonicalUrl = useMemo(() => `${SITE_URL}${location.pathname}`, [location.pathname]);

  const pageTitle = useMemo(() => {
    if (loading) return 'Loading Campaign | Ballotr';
    if (!campaign) return 'Campaign Not Found | Ballotr';
    return `${campaign.title} | Ballotr`;
  }, [campaign, loading]);

  const pageDescription = useMemo(() => {
    if (loading) {
      return 'Loading a Ballotr campaign tied to current political news and civic action.';
    }

    if (!campaign) {
      return 'This Ballotr campaign could not be found.';
    }

    const fromDescription = truncateDescription(campaign.description, 160);
    if (fromDescription) return fromDescription;

    if (campaign.source) {
      return `Read this Ballotr campaign sourced from ${campaign.source} and choose whether to support or oppose it.`;
    }

    return 'Read this Ballotr campaign and choose whether to support or oppose it through civic action.';
  }, [campaign, loading]);

  const metaImageUrl = useMemo(() => {
    if (!campaign?.image || showFallback) return DEFAULT_SOCIAL_IMAGE;
    return toAbsoluteUrl(campaign.image);
  }, [campaign?.image, showFallback]);

  const robotsContent = useMemo(() => {
    if (!loading && !campaign) return 'noindex,nofollow';
    return 'index,follow';
  }, [campaign, loading]);

  const shareTitle = useMemo(() => {
    if (!campaign?.title) return 'Ballotr';
    return `${campaign.title} | Ballotr`;
  }, [campaign?.title]);

  const shareText = useMemo(() => {
    if (!campaign?.title) return 'Read the issue, take a side, and act on Ballotr.';
    return `Read the issue, take a side, and act on Ballotr: ${campaign.title}`;
  }, [campaign?.title]);

  const shareUrl = canonicalUrl;
  const canUseNativeShare =
    isMobileViewport &&
    typeof navigator !== 'undefined' &&
    typeof navigator.share === 'function';

  useEffect(() => {
    const handleResize = () => {
      setIsMobileViewport(window.innerWidth < MOBILE_BREAKPOINT);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!isMobileViewport) {
      setShowMobileShareSheet(false);
    } else {
      setShowDesktopShareMenu(false);
    }
  }, [isMobileViewport]);

  useEffect(() => {
    setImgFailed(false);
  }, [campaign?.image, campaign?.id]);

  useEffect(() => {
    supabase.auth.getSession().then(() => {});
  }, []);

  useEffect(() => {
    const passedCampaign = location.state?.campaign;
    if (passedCampaign && passedCampaign.id === id) {
      setCampaign(passedCampaign);
      setLoading(false);
    } else if (id) {
      (async () => {
        const { data } = await supabase.from('campaigns').select('*').eq('id', id).single();
        setCampaign(data || null);
        setLoading(false);
      })();
    }
  }, [id, location.state]);

  useEffect(() => {
    const returnStatus = searchParams.get('return_status');
    if (!returnStatus) return;

    if (returnStatus === 'completed') {
      toast.success('Thanks for confirming your contribution.');
    } else if (returnStatus === 'canceled') {
      toast('Contribution redirect marked as not completed.');
    } else {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('return_status');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!id) return;

    trackPageView('campaign_detail', { campaign_id: id });

    trackEvent(ANALYTICS_EVENTS.CAMPAIGN_DETAIL_VIEWED, {
      campaign_id: id,
      has_prefetched_campaign: Boolean(location.state?.campaign),
    });
  }, [id, location.state]);

  useEffect(() => {
    if (!id) return;

    (async () => {
      const { data: intents, error } = await supabase
        .from('campaign_comments_public')
        .select(
          'id, campaign_id, intent_type, amount_cents, comment, created_at, first_name, last_initial'
        )
        .eq('campaign_id', id)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching campaign highlights:', error);
        setHighlights({});
        return;
      }

      if (!intents || intents.length === 0) {
        setHighlights({});
        return;
      }

      const formatName = (row: any) => {
        const first = row?.first_name || '';
        const lastInitial = row?.last_initial ? `${row.last_initial}.` : '';
        const full = `${first} ${lastInitial}`.trim();
        return full.length > 0 ? full : 'Anonymous';
      };

      const amountDollars = (row: any) => Math.round(((row?.amount_cents as number) || 0) / 100);

      const top = [...intents].sort(
        (a: any, b: any) => ((b.amount_cents as number) || 0) - ((a.amount_cents as number) || 0)
      )[0];
      const recent = intents[intents.length - 1];
      const first = intents[0];

      setHighlights({
        top: {
          name: formatName(top),
          amount: amountDollars(top),
          label: 'Top reported amount',
        },
        recent: {
          name: formatName(recent),
          amount: amountDollars(recent),
          label: 'Most recent activity',
        },
        first: {
          name: formatName(first),
          amount: amountDollars(first),
          label: 'First recorded activity',
        },
      });
    })();
  }, [id]);

  useEffect(() => {
    if (!id) return;

    (async () => {
      const { data: c, error } = await supabase
        .from('campaigns')
        .select('upvotes, downvotes')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Vote counter fetch error:', error);
        return;
      }

      if (!c) {
        console.error('Vote counter fetch returned no campaign row for id:', id);
        return;
      }

      setUpvoteCount(c.upvotes ?? 0);
      setDownvoteCount(c.downvotes ?? 0);
    })();
  }, [id]);

  useEffect(() => {
    if (!id || !user) {
      setUserVote(null);
      return;
    }

    (async () => {
      const { data: myVote } = await supabase
        .from('votes')
        .select('vote_type')
        .eq('campaign_id', id)
        .eq('user_id', user.id)
        .maybeSingle();

      setUserVote((myVote?.vote_type as 'upvote' | 'downvote') ?? null);
    })();
  }, [id, user]);

  useEffect(() => {
    setShowDesktopShareMenu(false);
    setShowMobileShareSheet(false);
  }, [location.pathname]);

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  const handleAddComment = async () => {
    if (!user) {
      trackEvent(ANALYTICS_EVENTS.SIGNIN_PROMPT_SHOWN, { campaign_id: id });
      setShowPrompt(true);
      return;
    }

    if (!canComment) {
      toast('Please contribute to this campaign before commenting.');
      return;
    }

    const trimmed = newComment.trim();
    if (!trimmed) {
      toast.error('Please enter a comment before posting.');
      return;
    }

    try {
      await addComment(trimmed);
      setNewComment('');
      toast.success('Comment posted.');
      commentTextareaRef.current?.focus();
    } catch (error: any) {
      console.error('Error posting campaign comment:', error);
      toast.error(error?.message || 'Unable to post comment.');
    }
  };

  const handleVote = async (type: 'upvote' | 'downvote') => {
    trackEvent(ANALYTICS_EVENTS.CAMPAIGN_VOTE_CLICKED, {
      campaign_id: id,
      vote_type: type,
      location: 'campaign_detail',
    });

    if (!user) {
      trackEvent(ANALYTICS_EVENTS.SIGNIN_PROMPT_SHOWN, { campaign_id: id });
      setShowPrompt(true);
      return;
    }

    const sameClick = userVote === type;
    const prev = userVote;

    if (sameClick) {
      setUserVote(null);

      const { error } = await supabase
        .from('votes')
        .delete()
        .eq('campaign_id', id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Vote delete error:', error);
        setUserVote(prev);
        return;
      }
    } else {
      setUserVote(type);

      const { error } = await supabase
        .from('votes')
        .upsert(
          { campaign_id: id, user_id: user.id, vote_type: type },
          { onConflict: 'campaign_id,user_id' }
        );

      if (error) {
        console.error('Vote upsert error:', error);
        setUserVote(prev);
        return;
      }
    }

    const { data: c, error: cErr } = await supabase
      .from('campaigns')
      .select('upvotes, downvotes')
      .eq('id', id)
      .single();

    if (cErr) {
      console.error('Vote refresh error:', cErr);
      return;
    }

    if (!c) {
      console.error('Vote refresh returned no campaign row for id:', id);
      return;
    }

    setUpvoteCount(c.upvotes ?? 0);
    setDownvoteCount(c.downvotes ?? 0);
  };

  const closeShareUi = () => {
    setShowDesktopShareMenu(false);
    setShowMobileShareSheet(false);
  };

  const trackShare = (method: 'native' | 'x' | 'facebook' | 'text' | 'copy_link') => {
    trackEvent(ANALYTICS_EVENTS.CAMPAIGN_SHARED, {
      campaign_id: id,
      share_method: method,
      location: 'campaign_detail',
    });
  };

  const copyShareLink = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.setAttribute('readonly', 'true');
        textArea.style.position = 'absolute';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      trackShare('copy_link');
      setCopied(true);
      closeShareUi();
    } catch (error) {
      console.error('Copy share link failed:', error);
      toast.error('Unable to copy link right now.');
    }
  };

  const openShareWindow = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer,width=640,height=720');
    closeShareUi();
  };

  const handleShareOption = async (method: 'x' | 'facebook' | 'text' | 'copy_link') => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedText = encodeURIComponent(`${shareText} ${shareUrl}`);

    switch (method) {
      case 'x':
        trackShare('x');
        openShareWindow(`https://twitter.com/intent/tweet?text=${encodedText}`);
        return;
      case 'facebook':
        trackShare('facebook');
        openShareWindow(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`);
        return;
      case 'text':
        trackShare('text');
        closeShareUi();
        window.location.href = `sms:?&body=${encodedText}`;
        return;
      case 'copy_link':
        await copyShareLink();
        return;
      default:
        return;
    }
  };

  const handleShare = async () => {
    if (canUseNativeShare) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });

        trackShare('native');
        return;
      } catch (error: any) {
        if (error?.name === 'AbortError') return;
        console.error('Native share failed, falling back to sheet:', error);
        setShowMobileShareSheet(true);
        setShowDesktopShareMenu(false);
        return;
      }
    }

    if (isMobileViewport) {
      setShowMobileShareSheet((prev) => !prev);
      setShowDesktopShareMenu(false);
      return;
    }

    setShowDesktopShareMenu((prev) => !prev);
    setShowMobileShareSheet(false);
  };

  const handleContributeClick = (type: 'support' | 'oppose') => {
    trackEvent(
      type === 'support'
        ? ANALYTICS_EVENTS.CAMPAIGN_SUPPORT_CLICKED
        : ANALYTICS_EVENTS.CAMPAIGN_OPPOSE_CLICKED,
      {
        campaign_id: id,
        location: 'campaign_detail',
      }
    );

    if (!user) {
      trackEvent(ANALYTICS_EVENTS.SIGNIN_PROMPT_SHOWN, { campaign_id: id });
      setShowPrompt(true);
    } else if (profile?.preferred_org) {
      setPendingAction(type);

      trackEvent(ANALYTICS_EVENTS.SAVED_ORG_CONFIRM_SHOWN, {
        campaign_id: id,
        org_id: profile.preferred_org.id,
      });

      setShowOrgConfirm(true);
    } else {
      setPendingAction(type);

      trackEvent(ANALYTICS_EVENTS.ORG_SELECT_MODAL_SHOWN, {
        campaign_id: id,
      });

      setShowOrgSelect(true);
    }
  };

  const proceedWithSavedOrg = () => {
    setShowOrgConfirm(false);
    if (pendingAction) {
      navigate(`/contribute?campaignId=${campaign.id}&action=${pendingAction}`, {
        state: { campaign },
      });
    }
  };

  const handleSourceClick = () => {
    trackEvent(ANALYTICS_EVENTS.CAMPAIGN_SOURCE_CLICKED, {
      campaign_id: id,
      source: campaign?.source,
      source_url: campaign?.source_url,
      location: 'campaign_detail',
    });
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
          <meta name="robots" content={robotsContent} />
          <link rel="canonical" href={canonicalUrl} />
          <meta property="og:site_name" content="Ballotr" />
          <meta property="og:type" content="website" />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={pageDescription} />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:image" content={DEFAULT_SOCIAL_IMAGE} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:title" content={pageTitle} />
          <meta name="twitter:description" content={pageDescription} />
          <meta name="twitter:image" content={DEFAULT_SOCIAL_IMAGE} />
        </Helmet>

        <div className="text-center mt-10 text-gray-500">Loading campaign...</div>
      </>
    );
  }

  if (!campaign) {
    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
          <meta name="robots" content={robotsContent} />
          <link rel="canonical" href={canonicalUrl} />
          <meta property="og:site_name" content="Ballotr" />
          <meta property="og:type" content="website" />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={pageDescription} />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:image" content={DEFAULT_SOCIAL_IMAGE} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:title" content={pageTitle} />
          <meta name="twitter:description" content={pageDescription} />
          <meta name="twitter:image" content={DEFAULT_SOCIAL_IMAGE} />
        </Helmet>

        <main className="min-h-screen flex items-center justify-center bg-secondary text-gray-600">
          <div className="text-center">
            <h1 className="text-xl font-semibold mb-2">Campaign not found</h1>
            <p>Please check the URL or return to the homepage.</p>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content={robotsContent} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:site_name" content="Ballotr" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={metaImageUrl} />
        {campaign.created_at && (
          <meta property="article:published_time" content={campaign.created_at} />
        )}
        {campaign.source && <meta property="article:author" content={campaign.source} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={metaImageUrl} />
      </Helmet>

      <main className="min-h-screen bg-secondary p-4">
        <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-md overflow-visible grid grid-cols-1 md:grid-cols-3 gap-6 p-6">
          <div className="col-span-2">
            <h1 className="text-2xl font-bold text-textPrimary mb-4">{campaign.title}</h1>

            <div className="relative mb-4">
              {campaign.source_url ? (
                <a
                  href={campaign.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleSourceClick}
                >
                  {showFallback ? (
                    <CampaignFallbackImage
                      title={campaign.title}
                      category={campaign.category}
                      source={campaign.source}
                      heightClassName="max-h-[400px] min-h-[240px]"
                    />
                  ) : (
                    <img
                      src={campaign.image}
                      alt={campaign.title}
                      className="w-full rounded-md object-cover max-h-[400px]"
                      onError={() => setImgFailed(true)}
                    />
                  )}
                  <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 p-1 rounded-full">
                    <ExternalLinkIcon className="text-white w-4 h-4" />
                  </div>
                </a>
              ) : showFallback ? (
                <CampaignFallbackImage
                  title={campaign.title}
                  category={campaign.category}
                  source={campaign.source}
                  heightClassName="max-h-[400px] min-h-[240px]"
                />
              ) : (
                <img
                  src={campaign.image}
                  alt={campaign.title}
                  className="w-full rounded-md object-cover max-h-[400px]"
                  onError={() => setImgFailed(true)}
                />
              )}
            </div>

            <p className="text-sm text-textSecondary mb-1">By {campaign.author_name}</p>
            {campaign.source && (
              <p className="text-sm text-textSecondary mb-4">Source: {campaign.source}</p>
            )}
            <p className="text-base text-textPrimary whitespace-pre-line mb-4">
              {campaign.description}
            </p>

            <div className="mt-2 flex flex-wrap items-center gap-3">
              <Button
                variant={userVote === 'upvote' ? undefined : 'outline'}
                size="sm"
                onClick={() => handleVote('upvote')}
                className={`flex items-center min-w-[42px] ${userVote === 'upvote' ? 'bg-[#0088CC] text-white' : ''}`}
              >
                <ArrowUpIcon className={`w-4 h-4 ${showVoteCounts ? 'mr-1' : ''}`} />
                {showVoteCounts && upvoteCount}
              </Button>

              <Button
                variant={userVote === 'downvote' ? undefined : 'outline'}
                size="sm"
                onClick={() => handleVote('downvote')}
                className={`flex items-center min-w-[42px] ${userVote === 'downvote' ? 'bg-[#0088CC] text-white' : ''}`}
              >
                <ArrowDownIcon className={`w-4 h-4 ${showVoteCounts ? 'mr-1' : ''}`} />
                {showVoteCounts && downvoteCount}
              </Button>

              <div className="relative">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleShare}
                  className="flex items-center"
                  aria-expanded={showDesktopShareMenu || showMobileShareSheet}
                  aria-haspopup="menu"
                >
                  <Share2Icon className="w-4 h-4 inline mr-1" />
                  Share
                  {!isMobileViewport && <ChevronDownIcon className="w-4 h-4 ml-1" />}
                </Button>

                <CampaignShareMenu
                  isDesktopOpen={showDesktopShareMenu}
                  isMobileOpen={showMobileShareSheet}
                  copied={copied}
                  onClose={closeShareUi}
                  onShareOption={handleShareOption}
                />
              </div>

              {copied && <span className="text-sm text-green-600 font-medium">Copied!</span>}
            </div>

            <section className="mt-8">
              <h2 className="text-xl font-semibold mb-1">Comments ({comments.length})</h2>

              {!user ? (
                <p className="text-gray-500 mt-2">
                  Please contribute to this campaign to share your comments.
                </p>
              ) : canComment ? (
                <div className="mt-4 mb-6 rounded-lg border border-blue-100 bg-blue-50/60 p-4">
                  <label
                    htmlFor="campaign-comment"
                    className="block text-sm font-medium text-textPrimary mb-2"
                  >
                    Add another comment
                  </label>

                  <p className="text-sm text-gray-600 mb-3">
                    You’ve previously supported/opposed this campaign, so commenting is unlocked.
                  </p>

                  <textarea
                    id="campaign-comment"
                    ref={commentTextareaRef}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    rows={4}
                    maxLength={750}
                    placeholder="Share your thoughts on this campaign..."
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />

                  <div className="mt-2 flex items-center justify-between">
                    <p className="text-xs text-gray-500">{newComment.length}/750</p>

                    <Button
                      variant="primary"
                      onClick={handleAddComment}
                      disabled={submittingComment || !newComment.trim()}
                    >
                      {submittingComment ? 'Posting...' : 'Post Comment'}
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 mt-2">
                  Please contribute to this campaign to unlock commenting.
                </p>
              )}

              {loadingComments ? (
                <p className="text-gray-500 italic mt-2">Loading comments...</p>
              ) : comments.length > 0 ? (
                <CommentFeed comments={comments} />
              ) : (
                <p className="text-gray-500 italic mt-2">
                  No comments yet — be the first to share your thoughts!
                </p>
              )}
            </section>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-md p-6 flex flex-col justify-start gap-4 self-start max-h-[500px]">
            <Button variant="support" onClick={() => handleContributeClick('support')} fullWidth>
              Support
            </Button>
            <Button variant="secondary" onClick={() => handleContributeClick('oppose')} fullWidth>
              Oppose
            </Button>
            {Object.keys(highlights).length > 0 ? (
              <ContributionSidebar highlights={highlights} />
            ) : (
              <div className="p-4 bg-gray-100 rounded-lg text-center text-sm text-gray-500">
                Be the first to support this campaign.
              </div>
            )}
          </div>
        </div>

        {showPrompt && <SignInPromptModal onClose={() => setShowPrompt(false)} />}
        {showOrgConfirm && profile?.preferred_org && (
          <OrgConfirmModal
            savedOrg={profile.preferred_org}
            onProceed={proceedWithSavedOrg}
            onChange={() => {
              setShowOrgConfirm(false);
              setShowOrgSelect(true);
            }}
            onClose={() => setShowOrgConfirm(false)}
          />
        )}
        {showOrgSelect && pendingAction && (
          <OrgSelectModal
            isOpen={true}
            onClose={() => setShowOrgSelect(false)}
            campaignId={id!}
            action={pendingAction}
          />
        )}
      </main>
    </>
  );
};

export default CampaignDetail;